<template>
  <AppHeader />
  <div class="credits-tracker-page min-h-screen bg-blue-50 pb-20 text-left">
    <!-- Header -->
    <div class="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 py-8 shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center">
          <button @click="$router.back()" class="mr-4 text-white hover:text-yellow-400 transition-colors">
            <span class="material-symbols-outlined text-3xl">arrow_back</span>
          </button>
          <div class="text-left">
            <h1 class="text-3xl font-bold text-white mb-1 text-left">Credits Tracker</h1>
            <p class="text-blue-100 text-sm text-left">Monitor your academic progress and plan your journey</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-20">
      <div class="w-12 h-12 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin mb-4"></div>
      <p class="text-blue-800 font-medium">Loading your credits data...</p>
    </div>

    <!-- Main content -->
    <div v-else class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Course Info Card -->
      <div class="bg-white rounded-lg shadow p-5 sm:p-6 mb-6 border border-blue-100 text-left">
        <div class="flex items-start gap-4">
          <div class="w-14 h-14 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 flex items-center justify-center flex-shrink-0">
            <span class="material-symbols-outlined text-white text-2xl">school</span>
          </div>
          <div class="flex-1 min-w-0 text-left">
            <h2 class="text-2xl font-bold text-gray-900 mb-1 text-left">
              {{ profile?.university?.courseName || 'Course Name Not Set' }}
            </h2>
            <p class="text-base text-gray-600 mb-2 text-left">{{ profile?.university?.faculty || 'Faculty Not Set' }}</p>
            <div class="flex flex-wrap gap-2 mt-3">
              <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                <span class="material-symbols-outlined text-sm mr-1">stairs</span>
                {{ profile?.university?.levelOfStudy || 'Level Not Set' }}
              </span>
              <span v-if="profile?.university?.studyYear" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                <span class="material-symbols-outlined text-sm mr-1">calendar_month</span>
                Year {{ profile.university.studyYear }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Credits Overview -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <!-- Current Credits -->
        <div class="bg-white rounded-lg shadow p-5 border border-blue-100 text-left">
          <div class="flex items-center justify-between mb-3">
            <div class="w-12 h-12 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center">
              <span class="material-symbols-outlined text-2xl">assignment_turned_in</span>
            </div>
            <span class="text-3xl font-bold text-gray-900">{{ currentCreditsDisplay }}</span>
          </div>
          <p class="text-sm font-semibold text-gray-900 mb-1 text-left">Current Credits</p>
          <p class="text-xs text-gray-500 text-left">From {{ knownCreditsCount }} of {{ moduleCredits.length }} {{ moduleCredits.length === 1 ? 'module' : 'modules' }}</p>
        </div>

        <!-- Target Credits -->
        <div class="bg-white rounded-lg shadow p-5 border border-blue-100 text-left">
          <div class="flex items-center justify-between mb-3">
            <div class="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <span class="material-symbols-outlined text-2xl">flag</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-3xl font-bold text-gray-900">{{ targetCreditsDisplay }}</span>
              <button
                type="button"
                @click="startEditingTargetCredits"
                class="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 text-blue-800 text-xs font-semibold hover:bg-blue-100 transition-colors"
              >
                <span class="material-symbols-outlined text-sm mr-1">edit</span>
                {{ targetCredits === null ? 'Set' : 'Edit' }}
              </button>
            </div>
          </div>
          <p class="text-sm font-semibold text-gray-900 mb-1 text-left">Target Credits</p>
          <p class="text-xs text-gray-500 text-left">{{ targetCreditsCaption }}</p>
        </div>

        <!-- Remaining Credits -->
        <div 
          class="bg-white rounded-lg shadow p-5 border border-blue-100 text-left"
        >
          <div class="flex items-center justify-between mb-3">
            <div
              class="w-12 h-12 rounded-full flex items-center justify-center"
              :class="remainingCredits === null || remainingCredits > 0 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'"
            >
              <span class="material-symbols-outlined text-2xl">
                {{ remainingCredits === null || remainingCredits > 0 ? 'trending_up' : 'check_circle' }}
              </span>
            </div>
            <span class="text-3xl font-bold text-gray-900">{{ remainingCreditsDisplay }}</span>
          </div>
          <p class="text-sm font-semibold text-gray-900 mb-1 text-left">{{ remainingCredits === null || remainingCredits > 0 ? 'Remaining Credits' : 'Credits Complete!' }}</p>
          <p class="text-xs text-gray-500 text-left">{{ remainingCreditsCaption }}</p>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="bg-white rounded-lg shadow p-5 sm:p-6 mb-6 border border-blue-100">
        <div class="flex items-center justify-between mb-3 gap-3">
          <h3 class="font-bold text-gray-900 text-lg flex items-center text-left">
            <span class="material-symbols-outlined text-blue-800 mr-2">insights</span>
            Overall Progress
          </h3>
          <span class="text-2xl font-bold text-blue-800">{{ completionPercentageDisplay }}</span>
        </div>
        <div class="relative h-6 bg-gray-200 rounded-full overflow-hidden">
          <div 
            class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-800 via-blue-600 to-blue-500 transition-all duration-700 ease-out rounded-full flex items-center justify-end pr-2"
            :style="`width: ${completionBarWidth}%`"
          >
            <span v-if="completionPercentage !== null && completionPercentage > 10" class="text-xs font-bold text-white">{{ completionPercentage }}%</span>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 mt-3">
          <p class="text-sm text-gray-600 text-left">
            <span class="font-semibold text-blue-800">{{ currentCreditsDisplay }}</span> of <span class="font-semibold">{{ targetCreditsDisplay }}</span> credits completed
          </p>
          <p class="text-xs text-gray-500 text-left sm:text-right">
            {{ remainingCreditsMessage }}
          </p>
        </div>
      </div>

      <!-- Target Credits Setup -->
      <div v-if="targetCredits === null" class="bg-white border border-amber-200 rounded-lg shadow p-5 sm:p-6 mb-6">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div class="flex items-start gap-3">
            <div class="w-11 h-11 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <span class="material-symbols-outlined text-2xl">flag</span>
            </div>
            <div class="text-left">
              <h3 class="font-bold text-gray-900 text-base sm:text-lg">Set your target credits to track completion progress.</h3>
              <p class="text-sm text-gray-600 mt-1">Use the total credits required for your qualification or academic year.</p>
            </div>
          </div>
          <button
            type="button"
            @click="startEditingTargetCredits"
            class="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
          >
            <span class="material-symbols-outlined text-lg mr-2">edit</span>
            Set Target
          </button>
        </div>
      </div>

      <!-- Status Card with Guidelines -->
      <div v-else-if="creditStatus !== 'on-track'" class="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 rounded-xl shadow-md p-6 mb-8">
        <div class="flex items-start">
          <div class="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mr-4 flex-shrink-0">
            <span class="material-symbols-outlined text-yellow-600 text-2xl">info</span>
          </div>
          <div class="flex-1">
            <h3 class="font-bold text-yellow-900 mb-3 text-lg">
              {{ creditStatus === 'insufficient' ? 'Action Needed - Build Your Credits' : 'Great Progress - Keep Going!' }}
            </h3>
            <div class="space-y-3">
              <div 
                v-for="(guideline, index) in guidelines" 
                :key="index" 
                class="flex items-start bg-white bg-opacity-50 rounded-lg p-3"
              >
                <div class="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                  <span class="material-symbols-outlined text-yellow-700 text-base">{{ guideline.icon }}</span>
                </div>
                <span class="text-sm text-yellow-900 leading-relaxed">{{ guideline.text }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-xl shadow-md p-6 mb-8">
        <div class="flex items-start">
          <div class="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4 flex-shrink-0">
            <span class="material-symbols-outlined text-green-600 text-2xl">check_circle</span>
          </div>
          <div class="flex-1">
            <h3 class="font-bold text-green-900 mb-2 text-lg">Excellent Progress!</h3>
            <p class="text-sm text-green-800 leading-relaxed">
              You're on track to complete your course requirements. Keep up the great work and maintain your academic momentum!
            </p>
          </div>
        </div>
      </div>

      <!-- Modules List -->
      <div class="bg-white rounded-lg shadow border border-blue-100 overflow-hidden">
        <div class="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-5 border-b border-blue-200">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div class="flex items-center">
              <div class="w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center mr-3">
                <span class="material-symbols-outlined text-white text-xl">list_alt</span>
              </div>
              <div>
                <h2 class="text-lg font-bold text-gray-900">Current Modules</h2>
                <p class="text-xs text-gray-600">{{ moduleCredits.length }} {{ moduleCredits.length === 1 ? 'module' : 'modules' }} enrolled</p>
              </div>
            </div>
            <span class="inline-flex items-center self-start sm:self-center px-4 py-2 bg-blue-800 text-white text-sm font-semibold rounded-full">
              {{ currentCreditsLabel }}
            </span>
          </div>
        </div>

        <div v-if="moduleCredits.length === 0" class="p-12 text-center">
          <div class="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <span class="material-symbols-outlined text-gray-300 text-5xl">inbox</span>
          </div>
          <p class="text-gray-500 mb-1 font-medium">No modules found</p>
          <p class="text-sm text-gray-400 mb-6">Update your profile to add modules and track your credits</p>
          <router-link 
            to="/update-university" 
            class="inline-flex items-center px-6 py-3 bg-blue-800 text-white rounded-lg hover:bg-blue-900 transition-colors shadow-md"
          >
            <span class="material-symbols-outlined mr-2">add</span>
            Add Modules
          </router-link>
        </div>

        <div v-else class="divide-y divide-gray-100">
          <div 
            v-for="(module, index) in moduleCredits" 
            :key="module.key || index"
            class="px-5 sm:px-6 py-5 hover:bg-blue-50 transition-colors"
          >
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div class="flex items-center flex-1 min-w-0">
                <div class="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span class="material-symbols-outlined text-blue-800 text-xl">book</span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="font-semibold text-gray-900 text-left mb-1 truncate">{{ module.name }}</p>
                  <p class="text-xs text-gray-500 text-left">Module {{ index + 1 }} of {{ moduleCredits.length }}</p>
                </div>
              </div>
              <div class="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">
                <span class="inline-flex items-center justify-center min-w-0 px-4 py-2 rounded-full text-sm font-bold bg-blue-100 text-blue-800">
                  <span class="material-symbols-outlined text-lg mr-1.5">grade</span>
                  {{ module.credits === null ? 'Not set' : `${module.credits} credits` }}
                </span>
                <button
                  type="button"
                  @click="startEditingModuleCredit(module)"
                  class="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-blue-200 text-blue-800 text-sm font-semibold hover:bg-blue-100 transition"
                >
                  <span class="material-symbols-outlined text-base mr-1">edit</span>
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Info -->
      <div class="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-5">
        <div class="flex items-start">
          <div class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
            <span class="material-symbols-outlined text-blue-600 text-lg">info</span>
          </div>
          <div class="flex-1">
            <p class="text-sm text-blue-900 leading-relaxed">
              <span class="font-semibold">Note:</span> This tracker only uses credit values saved on your module, profile, or app settings data.
              Modules without credit metadata are shown as not set.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="creditEditSheetOpen"
    class="fixed inset-0 z-[1050] bg-black/40 flex items-end sm:items-center justify-center"
    @click.self="closeCreditEditSheet"
  >
    <aside class="bg-white w-full sm:max-w-md max-h-[92vh] rounded-t-lg sm:rounded-lg overflow-hidden flex flex-col text-left">
      <div class="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
        <div class="text-left">
          <h2 class="text-lg font-semibold text-gray-900 text-left">{{ creditEditTitle }}</h2>
          <p class="text-sm text-gray-500 text-left">{{ creditEditSubtitle }}</p>
        </div>
        <button
          type="button"
          class="h-9 w-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
          @click="closeCreditEditSheet"
          aria-label="Close credit editor"
          :disabled="creditEditSaving"
        >
          <span class="material-symbols-outlined text-xl">close</span>
        </button>
      </div>

      <form class="flex-1 overflow-y-auto p-4 space-y-4" @submit.prevent="saveActiveCreditEdit">
        <label class="block text-left">
          <span class="text-sm font-medium text-gray-700 text-left block">{{ creditEditInputLabel }}</span>
          <input
            :id="creditEditInputId"
            v-model="activeCreditFormValue"
            type="number"
            :min="creditEditMode === 'target' ? 1 : 0"
            step="1"
            inputmode="numeric"
            :placeholder="creditEditMode === 'target' ? 'e.g. 120' : 'Leave blank to clear'"
            class="mt-1 w-full border border-gray-200 rounded-lg px-3 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
            :disabled="creditEditSaving"
            ref="creditEditInput"
          />
        </label>

        <div v-if="creditEditMode === 'module'" class="rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900">
          Blank credits will clear the saved value for this module.
        </div>
      </form>

      <div class="p-4 border-t border-gray-100 grid grid-cols-2 gap-3">
        <button
          type="button"
          class="px-4 py-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold transition-colors"
          @click="closeCreditEditSheet"
          :disabled="creditEditSaving"
        >
          Cancel
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-semibold transition-colors disabled:opacity-60"
          @click="saveActiveCreditEdit"
          :disabled="creditEditSaving"
        >
          <span class="material-symbols-outlined text-lg mr-2">{{ creditEditSaving ? 'hourglass_top' : 'save' }}</span>
          {{ creditEditSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </aside>
  </div>

  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import { mapMutations, mapState } from 'vuex';
import { getUserProfile, updateUserUniversity } from '@/composables/useFirestore';
import { getKnownCreditsCount, getModuleCredits, getTargetCredits, normalizeModuleKey } from '@/utils/creditEstimator';

export default {
  components: {
    AppHeader,
    BottomNav
  },
  data() {
    return {
      targetCreditsForm: '',
      moduleCreditForms: {},
      savingTargetCredits: false,
      savingModuleCreditKey: '',
      editingModuleCreditKey: '',
      editingModule: null,
      creditEditMode: '',
      creditEditSheetOpen: false
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'loading', 'globalSettings']),
    
    moduleCredits() {
      if (!this.profile?.university?.modules || this.profile.university.modules.length === 0) {
        return [];
      }
      
      return getModuleCredits(this.profile.university.modules, this.moduleCreditsMap);
    },

    moduleCreditsMap() {
      return this.profile?.university?.moduleCredits || {};
    },
    
    currentCredits() {
      return this.moduleCredits.reduce((sum, module) => (
        module.credits === null ? sum : sum + module.credits
      ), 0);
    },

    currentCreditsDisplay() {
      return this.knownCreditsCount === 0 ? '-' : this.currentCredits;
    },

    currentCreditsLabel() {
      return this.knownCreditsCount === 0 ? 'Credits not set' : `${this.currentCredits} credits`;
    },

    knownCreditsCount() {
      return getKnownCreditsCount(this.profile?.university?.modules || [], this.moduleCreditsMap);
    },
    
    targetCredits() {
      return getTargetCredits(this.profile, this.globalSettings);
    },

    targetCreditsDisplay() {
      return this.targetCredits === null ? '-' : this.targetCredits;
    },

    hasProfileTargetCredits() {
      const targetCredits = this.profile?.university?.targetCredits;
      return targetCredits !== '' && targetCredits !== null && targetCredits !== undefined && Number(targetCredits) > 0;
    },

    targetCreditsCaption() {
      if (this.targetCredits === null) {
        return 'Set this to unlock progress tracking';
      }

      return this.hasProfileTargetCredits ? 'Saved on your profile' : 'From app settings';
    },

    creditEditTitle() {
      return this.creditEditMode === 'target' ? 'Target Credits' : 'Module Credits';
    },

    creditEditSubtitle() {
      if (this.creditEditMode === 'target') {
        return 'Set the credits needed for your qualification or academic year.';
      }

      return this.editingModule?.name || 'Update module credits';
    },

    creditEditInputLabel() {
      return this.creditEditMode === 'target' ? 'Target Credits' : 'Credits';
    },

    creditEditInputId() {
      return this.creditEditMode === 'target'
        ? 'targetCredits'
        : this.getModuleCreditInputId(this.editingModule || {});
    },

    creditEditSaving() {
      return this.creditEditMode === 'target'
        ? this.savingTargetCredits
        : this.savingModuleCreditKey === this.editingModule?.key;
    },

    activeCreditFormValue: {
      get() {
        if (this.creditEditMode === 'target') {
          return this.targetCreditsForm;
        }

        return this.moduleCreditForms[this.editingModule?.key] || '';
      },
      set(value) {
        if (this.creditEditMode === 'target') {
          this.targetCreditsForm = value;
          return;
        }

        if (!this.editingModule?.key) return;
        this.moduleCreditForms = {
          ...this.moduleCreditForms,
          [this.editingModule.key]: value
        };
      }
    },
    
    remainingCredits() {
      if (this.targetCredits === null || this.knownCreditsCount === 0) return null;
      return Math.max(0, this.targetCredits - this.currentCredits);
    },

    remainingCreditsDisplay() {
      return this.remainingCredits === null ? '-' : this.remainingCredits;
    },

    remainingCreditsCaption() {
      if (this.targetCredits === null) {
        return 'Needs target credits';
      }

      if (this.knownCreditsCount === 0) {
        return 'Needs module credits';
      }

      return this.remainingCredits > 0 ? 'Still needed' : 'Target achieved';
    },

    remainingCreditsMessage() {
      if (this.targetCredits === null) {
        return 'Target credits not set';
      }

      if (this.knownCreditsCount === 0) {
        return 'Set module credits to calculate progress';
      }

      return this.remainingCredits > 0 ? `${this.remainingCredits} credits to go` : 'All credits earned!';
    },
    
    completionPercentage() {
      if (this.targetCredits === null || this.targetCredits === 0 || this.knownCreditsCount === 0) return null;
      return Math.min(100, Math.round((this.currentCredits / this.targetCredits) * 100));
    },

    completionPercentageDisplay() {
      return this.completionPercentage === null ? 'Not set' : `${this.completionPercentage}%`;
    },

    completionBarWidth() {
      return this.completionPercentage === null ? 0 : this.completionPercentage;
    },
    
    creditStatus() {
      if (this.completionPercentage === null) return 'insufficient';
      const percentage = this.completionPercentage;
      if (percentage >= 90) return 'on-track';
      if (percentage >= 50) return 'moderate';
      return 'insufficient';
    },
    
    guidelines() {
      if (this.knownCreditsCount === 0) {
        return [
          { icon: 'edit', text: 'Set credits for each module below to calculate your current total.' },
          { icon: 'flag', text: 'Your target credits are ready once module credits are added.' }
        ];
      }

      const percentage = this.completionPercentage;
      
      if (percentage >= 90) {
        return [
          { icon: 'check_circle', text: 'You\'re on track to complete your degree requirements!' },
          { icon: 'auto_awesome', text: 'Consider exploring elective modules to broaden your knowledge.' }
        ];
      }
      
      if (percentage >= 50) {
        return [
          { icon: 'schedule', text: `You need ${this.remainingCredits} more credits to complete your course.` },
          { icon: 'person', text: 'Consult with your academic advisor to plan remaining modules.' },
          { icon: 'event', text: 'Check if summer/winter schools offer modules you need.' },
          { icon: 'trending_up', text: 'Maintain your current pace to graduate on time.' }
        ];
      }
      
      return [
        { icon: 'warning', text: `You need ${this.remainingCredits} credits to meet graduation requirements.` },
        { icon: 'event_available', text: 'Register for additional modules in the next semester.' },
        { icon: 'person', text: 'Schedule a meeting with your academic advisor immediately.' },
        { icon: 'school', text: 'Consider summer/winter school opportunities to catch up.' },
        { icon: 'groups', text: 'Join study groups to improve your academic performance.' },
        { icon: 'schedule', text: 'Create a detailed plan to complete remaining credits.' }
      ];
    }
  },
  watch: {
    targetCredits: {
      immediate: true,
      handler(targetCredits) {
        this.targetCreditsForm = targetCredits === null ? '' : String(targetCredits);
      }
    }
  },
  methods: {
    ...mapMutations(['setProfile']),

    startEditingTargetCredits() {
      this.targetCreditsForm = this.targetCredits === null ? '' : String(this.targetCredits);
      this.creditEditMode = 'target';
      this.editingModule = null;
      this.editingModuleCreditKey = '';
      this.creditEditSheetOpen = true;
      this.focusCreditEditInput();
    },

    cancelEditingTargetCredits() {
      this.targetCreditsForm = this.targetCredits === null ? '' : String(this.targetCredits);
      this.closeCreditEditSheet();
    },

    isEditingModuleCredit(module) {
      return this.editingModuleCreditKey === module.key;
    },

    startEditingModuleCredit(module) {
      this.editingModuleCreditKey = module.key;
      this.editingModule = module;
      this.creditEditMode = 'module';
      this.moduleCreditForms = {
        ...this.moduleCreditForms,
        [module.key]: module.credits === null ? '' : String(module.credits)
      };
      this.creditEditSheetOpen = true;
      this.focusCreditEditInput();
    },

    cancelEditingModuleCredit() {
      this.closeCreditEditSheet();
    },

    closeCreditEditSheet(force = false) {
      if (this.creditEditSaving && !force) return;
      this.creditEditSheetOpen = false;
      this.creditEditMode = '';
      this.editingModule = null;
      this.editingModuleCreditKey = '';
    },

    focusCreditEditInput() {
      this.$nextTick(() => {
        this.$refs.creditEditInput?.focus();
      });
    },

    saveActiveCreditEdit() {
      if (this.creditEditMode === 'target') {
        return this.saveTargetCredits();
      }

      return this.saveModuleCredit(this.editingModule);
    },

    getModuleCreditInputId(module) {
      return `module-credit-${(module.key || module.name || 'module').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    },

    async saveModuleCredit(module) {
      if (!module) return;
      const moduleKey = normalizeModuleKey(module.name || module.key);

      if (!moduleKey) {
        this.$store.dispatch('showAlert', {
          message: 'Unable to save credits for this module.',
          type: 'error'
        });
        return;
      }

      const rawCredits = this.moduleCreditForms[module.key];
      const nextModuleCredits = { ...this.moduleCreditsMap };

      if (rawCredits === '' || rawCredits === null || rawCredits === undefined) {
        delete nextModuleCredits[moduleKey];
      } else {
        const credits = Number(rawCredits);

        if (!Number.isFinite(credits) || credits < 0) {
          this.$store.dispatch('showAlert', {
            message: 'Module credits must be 0 or more.',
            type: 'error'
          });
          return;
        }

        nextModuleCredits[moduleKey] = credits;
      }

      if (!this.user?.uid || !this.profile) {
        this.$store.dispatch('showAlert', {
          message: 'Please sign in before updating module credits.',
          type: 'error'
        });
        return;
      }

      this.savingModuleCreditKey = module.key;

      try {
        const updatedUniversity = {
          ...(this.profile.university || {}),
          moduleCredits: nextModuleCredits
        };

        await updateUserUniversity(this.user.uid, updatedUniversity);
        const refreshedProfile = await getUserProfile(this.user.uid);
        this.setProfile(refreshedProfile || {
          ...this.profile,
          university: updatedUniversity
        });
        this.closeCreditEditSheet(true);
        this.$store.dispatch('showAlert', {
          message: rawCredits === '' ? 'Module credits cleared.' : 'Module credits saved successfully!',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to save module credits.',
          type: 'error'
        });
      } finally {
        this.savingModuleCreditKey = '';
      }
    },

    async saveTargetCredits() {
      const targetCredits = Number(this.targetCreditsForm);

      if (!Number.isFinite(targetCredits) || targetCredits <= 0) {
        this.$store.dispatch('showAlert', {
          message: 'Please enter target credits greater than 0.',
          type: 'error'
        });
        return;
      }

      if (!this.user?.uid || !this.profile) {
        this.$store.dispatch('showAlert', {
          message: 'Please sign in before updating target credits.',
          type: 'error'
        });
        return;
      }

      this.savingTargetCredits = true;

      try {
        const updatedUniversity = {
          ...(this.profile.university || {}),
          targetCredits
        };

        await updateUserUniversity(this.user.uid, updatedUniversity);
        const refreshedProfile = await getUserProfile(this.user.uid);
        this.setProfile(refreshedProfile || {
          ...this.profile,
          university: updatedUniversity
        });
        this.closeCreditEditSheet(true);
        this.$store.dispatch('showAlert', {
          message: 'Target credits saved successfully!',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to save target credits.',
          type: 'error'
        });
      } finally {
        this.savingTargetCredits = false;
      }
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
